import {
  BusinessCenter,
  Description,
  DirectionsBoat,
  Group,
  Receipt,
  Route,
  Business,
  Refresh as RefreshIcon,
  CloudSync,
  CalendarMonth,
  CheckCircle,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, memo } from 'react';
import { DashboardService } from '../../services/dashboardService';
import { VesselFinderStatusService } from '../../services/vesselFinderStatusService';
import { useUserScope } from '../../hooks/useUserScope';
import { ToastService } from '../../services/toastService';

// Below this remaining-credit count the credits card turns amber as a warning.
const VESSELFINDER_LOW_CREDIT_THRESHOLD = 1000;

// The backend normalizes the expiration to RFC3339, but guard against
// unparseable values by falling back to the raw string (and '—' when empty).
const formatExpirationDate = (value?: string): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

const StatCard = memo(
  ({
    title,
    value,
    icon,
    color = 'primary',
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" sx={{ height: '100%' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              backgroundColor: `${color}.main`,
              color: 'white',
              borderRadius: 2,
              p: 1,
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
            <Typography color="text.secondary" gutterBottom>
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
);

export const DashboardPage = () => {
  const { effectiveUserId } = useUserScope();

  const {
    data: stats,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dashboard-stats', { userId: effectiveUserId }],
    queryFn: () => DashboardService.getDashboardStats(effectiveUserId || undefined),
    staleTime: 30_000, // Cache for 30 seconds
  });

  const {
    data: vesselFinderStatus,
    isLoading: isVfLoading,
    isError: isVfError,
    error: vfError,
    refetch: refetchVf,
    isFetching: isVfFetching,
  } = useQuery({
    queryKey: ['vesselfinder-status'],
    queryFn: () => VesselFinderStatusService.getStatus(),
    staleTime: 60_000, // Cache for 60 seconds; avoids hammering the proxy on nav
    retry: false,
  });

  // Handle errors with toast notifications
  useEffect(() => {
    if (isError && error) {
      console.error('Failed to load dashboard stats:', error);
      ToastService.handleApiError(error, 'Dashboard Statistics');
    }
  }, [isError, error]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !stats) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load dashboard statistics.
          {error instanceof Error ? ` Error: ${error.message}` : ' Please try again later.'}
        </Alert>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Welcome to the ETS Navigator Admin Panel. Here's an overview of your system.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Total Vessels"
            value={stats.vessels}
            icon={<DirectionsBoat />}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Total Voyages"
            value={stats.voyages}
            icon={<Route />}
            color="secondary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Active Charterers"
            value={stats.charterers}
            icon={<Group />}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Total Contracts"
            value={stats.contracts}
            icon={<Description />}
            color="warning"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Pending Invoices"
            value={stats.invoices}
            icon={<Receipt />}
            color="error"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Active Procurements"
            value={stats.procurements}
            icon={<BusinessCenter />}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Total Entities"
            value={stats.entities}
            icon={<Business />}
            color="secondary"
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 4,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">VesselFinder credits</Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => refetchVf()}
          disabled={isVfFetching}
        >
          Refresh
        </Button>
      </Box>

      {isVfLoading ? (
        <Box display="flex" alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            Loading VesselFinder status…
          </Typography>
        </Box>
      ) : isVfError || !vesselFinderStatus ? (
        <Alert severity="warning">
          Could not load VesselFinder status.
          {vfError instanceof Error ? ` Error: ${vfError.message}` : ''}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              title="Remaining Credits"
              value={vesselFinderStatus.remaining_credits.toLocaleString()}
              icon={<CloudSync />}
              color={
                vesselFinderStatus.remaining_credits < VESSELFINDER_LOW_CREDIT_THRESHOLD
                  ? 'warning'
                  : 'primary'
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              title="Expiration Date"
              value={formatExpirationDate(vesselFinderStatus.expiration_date)}
              icon={<CalendarMonth />}
              color="secondary"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard
              title="API Status"
              value={vesselFinderStatus.status || 'unknown'}
              icon={<CheckCircle />}
              color={vesselFinderStatus.status === 'active' ? 'success' : 'error'}
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Activity feed will be implemented here
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            <Typography variant="body2" color="text.secondary">
              System status indicators will be shown here
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
